import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import BankTransferPayment from '../components/BankTransferPayment';
import { supabase } from '../lib/supabase';
import { signUp } from '../services/authService';
import {
  User,
  MapPin,
  Bank,
  CheckCircle,
  ArrowRight,
  ArrowLeft,
  Lock,
  Eye,
  EyeSlash,
  ShoppingCart
} from 'phosphor-react';

const CheckoutFlow = ({ onComplete, onCancel }) => {
  const { t } = useTranslation();
  const { user, signIn } = useAuth();
  const { items, getCartTotal, getCartSubtotal, getCartShipping, getCartDiscount, getDiscountItems, clearCart } = useCart();

  const [currentStep, setCurrentStep] = useState(1);
  const [checkoutType, setCheckoutType] = useState(null); // 'login', 'guest', or 'account'
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [userProfile, setUserProfile] = useState(null);
  const [newlyCreatedUserId, setNewlyCreatedUserId] = useState(null); // Store user ID from account creation
  // Reserved from the DB sequence once per checkout session, so it's
  // guaranteed unique and matches the order that eventually gets saved.
  const [orderNumber, setOrderNumber] = useState(null);

  // Reserve a unique order number as soon as checkout opens, well before
  // the customer reaches the payment step where it's displayed.
  useEffect(() => {
    supabase.rpc('next_order_number').then(({ data, error: rpcError }) => {
      if (rpcError) {
        console.error('Error reserving order number:', rpcError);
        return;
      }
      setOrderNumber(data);
    });
  }, []);

  // Login form data
  const [loginData, setLoginData] = useState({
    email: '',
    password: ''
  });
  
  // Guest checkout data
  const [guestData, setGuestData] = useState({
    email: '',
    first_name: '',
    last_name: '',
    street_and_housenumber: '',
    city: '',
    postal_code: '',
    country: 'Belgium',
    phone: ''
  });
  
  // Account creation data (when checkbox is checked)
  const [accountData, setAccountData] = useState({
    password: '',
    confirmPassword: ''
  });
  
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [createAccount, setCreateAccount] = useState(false);

  // Steps configuration - 3 steps (payment and confirmation are combined)
  const steps = [
    { id: 1, title: t('checkout.step1'), description: t('checkout.step1Desc') },
    { id: 2, title: t('checkout.step2'), description: t('checkout.step2Desc') },
    { id: 3, title: t('checkout.step3'), description: t('checkout.step3Desc') }
  ];

  // Handle user authentication state changes
  useEffect(() => {
    console.log('=== USER AUTH STATE CHANGE ===');
    console.log('User:', user);
    console.log('Checkout Type:', checkoutType);
    console.log('Current Step:', currentStep);
    
    if (user && checkoutType === 'account' && currentStep === 2) {
      // User has been authenticated after account creation
      console.log('✅ User authenticated after account creation:', user.id);

      // Update any existing payment records to link them to the new user
      updatePaymentRecordsForNewUser(user.id, guestData.email);
    }
  }, [user, checkoutType, currentStep, guestData.email]);

  // Function to update payment records when a guest creates an account
  const updatePaymentRecordsForNewUser = async (userId, email) => {
    try {
      console.log('=== UPDATING PAYMENT RECORDS ===');
      console.log('User ID:', userId);
      console.log('Email:', email);
      
      // First, check what payment records exist
      const { data: existingRecords, error: fetchError } = await supabase
        .from('user_payments')
        .select('*')
        .eq('user_email', email)
        .is('user_id', null);
      
      console.log('Existing payment records to update:', existingRecords);
      
      if (fetchError) {
        console.error('Error fetching payment records:', fetchError);
        return;
      }
      
      if (!existingRecords || existingRecords.length === 0) {
        console.log('No payment records found to update');
        return;
      }
      
      // Update any payment records that were created with null user_id but matching email
      const { data, error } = await supabase
        .from('user_payments')
        .update({ user_id: userId })
        .eq('user_email', email)
        .is('user_id', null)
        .select();

      if (error) {
        console.error('Error updating payment records:', error);
      } else if (data && data.length > 0) {
        console.log('✅ Successfully updated payment records for new user:', data);
      } else {
        console.log('No payment records were updated');
      }
    } catch (err) {
      console.error('Error in updatePaymentRecordsForNewUser:', err);
    }
  };

  // Fetch user profile data when user is authenticated
  const fetchUserProfile = async (userId) => {
    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('id', userId)
        .single();
      
      if (error) {
        console.error('Error fetching user profile:', error);
        return null;
      }
      
      return data;
    } catch (err) {
      console.error('Error fetching user profile:', err);
      return null;
    }
  };

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  // Scroll to top when step changes (except for initial mount)
  useEffect(() => {
    if (currentStep > 1) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [currentStep]);

  // Auto-skip login step if user is already authenticated
  useEffect(() => {
    if (user && currentStep === 1) {
      setCheckoutType('login');
      setCurrentStep(2);
    }
  }, [user, currentStep]);

  // Fetch user profile when reaching step 2
  useEffect(() => {
    if (currentStep === 2 && user && !userProfile) {
      fetchUserProfile(user.id).then(profile => {
        setUserProfile(profile);
      });
    }
  }, [currentStep, user, userProfile]);

  const handleLogin = async () => {
    setError('');
    setLoading(true);
    
    try {
      await signIn(loginData.email, loginData.password);
      setCheckoutType('login');
      setCurrentStep(2);
      // Scroll to top when transitioning to step 2
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      setError(t('checkout.invalidCredentials'));
    } finally {
      setLoading(false);
    }
  };

  const handleGuestContinue = async () => {
    setError('');
    setLoading(true);
    
    try {
      // Validate guest data
      const required = ['email', 'first_name', 'last_name', 'street_and_housenumber', 'city', 'postal_code'];
      for (const field of required) {
        if (!guestData[field].trim()) {
          setError(t('checkout.requiredField', { field: t(`checkout.fields.${field}`) }));
          setLoading(false);
          return;
        }
      }
      
      // Email validation
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(guestData.email)) {
        setError(t('checkout.invalidEmail'));
        setLoading(false);
        return;
      }
      
      // Password validation if creating account
      if (createAccount) {
        if (accountData.password.length < 6) {
          setError(t('checkout.passwordTooShort'));
          setLoading(false);
          return;
        }
        
        if (accountData.password !== accountData.confirmPassword) {
          setError(t('checkout.passwordMismatch'));
          setLoading(false);
          return;
        }
        
        // Create account
        const userData = {
          first_name: guestData.first_name,
          last_name: guestData.last_name,
          full_name: `${guestData.first_name} ${guestData.last_name}`,
          phone: guestData.phone,
          street_and_housenumber: guestData.street_and_housenumber,
          city: guestData.city,
          postal_code: guestData.postal_code,
          country: guestData.country
        };
        
        const signUpData = await signUp(guestData.email, accountData.password, userData);
        
        if (signUpData.error) {
          setError(signUpData.error.message || 'Failed to create account');
          setLoading(false);
          return;
        }
        
        // If account creation was successful, we have the user ID directly
        if (signUpData.user) {
          console.log('Account created successfully, user ID:', signUpData.user.id);
          console.log('Email confirmed:', signUpData.user.email_confirmed_at);
          
          // Store the user ID for the payment process
          setNewlyCreatedUserId(signUpData.user.id);
          setCheckoutType('account');
          setCurrentStep(2);
          // Scroll to top when transitioning to step 2
          window.scrollTo({ top: 0, behavior: 'smooth' });
          
          console.log('User will be linked to payment records with ID:', signUpData.user.id);
        }
      } else {
        // Continue as guest
        setCheckoutType('guest');
        setCurrentStep(2);
        // Scroll to top when transitioning to step 2
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    } catch (err) {
      console.error('Error in guest checkout:', err);
      setError(err.message || 'An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleProceedToPayment = () => {
    setCurrentStep(3);
    // Scroll to top when transitioning to payment step
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
      setError('');
      // Scroll to top when going back
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleConfirmBankTransfer = async () => {
    try {
      setLoading(true);
      setError('');
      
      const orderData = {
        order_number: orderNumber,
        user_id: user?.id || newlyCreatedUserId || null, // Use newly created user ID if available
        user_email: user?.email || guestData.email,
        total_amount: getCartTotal(),
        currency: 'EUR',
        shipping_address: user ? userProfile : guestData,
        items: items.map(item => ({
          product_id: item.id,
          quantity: item.quantity,
          price: item.price,
          name: item.name
        })),
        checkout_type: checkoutType,
        shipping_cost: getCartShipping(),
        discount_amount: getCartDiscount()
      };

      // Save the order via the manual-payment edge function, which records
      // it as pending until we manually confirm the bank transfer arrived.
      console.log('Saving order via manual-payment:', orderData);

      const { data: savedOrder, error: functionError } = await supabase.functions.invoke(
        'manual-payment',
        { body: orderData }
      );

      if (functionError) {
        console.error('Order save failed:', functionError);
        throw new Error(`Failed to save order: ${functionError.message}`);
      }

      console.log('Order saved successfully:', savedOrder);
      
      // If this was an account creation checkout, update any payment records
      if (checkoutType === 'account' && user?.id) {
        await updatePaymentRecordsForNewUser(user.id, user.email || guestData.email);
      }
      
      // Clear cart
      clearCart();
      
      // Call the onComplete callback with order data
      onComplete(savedOrder);
      
    } catch (err) {
      console.error('Error completing order:', err);
      setError('Failed to complete order. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Step 1: Login or Guest Checkout
  const renderStep1 = () => (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h2 className="text-2xl font-semibold text-gray-900 mb-2">{t('checkout.step1Title')}</h2>
        <p className="text-gray-600">{t('checkout.step1Subtitle')}</p>
      </div>
      
      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-red-700 text-sm">{error}</p>
        </div>
      )}
      
      <div className="grid md:grid-cols-2 gap-8">
        {/* Left Column - Login */}
        <div className="bg-gray-50 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <Lock size={20} className="mr-2" />
            {t('checkout.login')}
          </h3>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">{t('checkout.email')}</label>
              <input
                type="email"
                value={loginData.email}
                onChange={(e) => setLoginData(prev => ({ ...prev, email: e.target.value }))}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ambre-500 focus:border-ambre-500"
                placeholder={t('checkout.enterEmail')}
                required
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">{t('checkout.password')}</label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={loginData.password}
                  onChange={(e) => setLoginData(prev => ({ ...prev, password: e.target.value }))}
                  className="w-full px-4 py-3 pr-12 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ambre-500 focus:border-ambre-500"
                  placeholder={t('checkout.enterPassword')}
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? <EyeSlash size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>
            
            <button
              onClick={handleLogin}
              disabled={loading}
              className="w-full px-6 py-3 bg-ambre-600 text-white rounded-lg hover:bg-ambre-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {loading ? t('checkout.signingIn') : t('checkout.login')}
            </button>
          </div>
        </div>
        
        {/* Right Column - Guest Checkout */}
        <div className="bg-gray-50 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <User size={20} className="mr-2" />
            {t('checkout.guestCheckout')}
          </h3>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">{t('checkout.email')}</label>
              <input
                type="email"
                value={guestData.email}
                onChange={(e) => setGuestData(prev => ({ ...prev, email: e.target.value }))}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ambre-500 focus:border-ambre-500"
                placeholder={t('checkout.enterEmail')}
                required
              />
            </div>
            
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">{t('checkout.firstName')}</label>
                <input
                  type="text"
                  value={guestData.first_name}
                  onChange={(e) => setGuestData(prev => ({ ...prev, first_name: e.target.value }))}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ambre-500 focus:border-ambre-500"
                  placeholder={t('checkout.firstName')}
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">{t('checkout.lastName')}</label>
                <input
                  type="text"
                  value={guestData.last_name}
                  onChange={(e) => setGuestData(prev => ({ ...prev, last_name: e.target.value }))}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ambre-500 focus:border-ambre-500"
                  placeholder={t('checkout.lastName')}
                  required
                />
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">{t('checkout.streetAndHousenumber')}</label>
              <input
                type="text"
                value={guestData.street_and_housenumber}
                onChange={(e) => setGuestData(prev => ({ ...prev, street_and_housenumber: e.target.value }))}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ambre-500 focus:border-ambre-500"
                placeholder={t('checkout.streetAndHousenumberPlaceholder')}
                required
              />
            </div>
            
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">{t('checkout.city')}</label>
                <input
                  type="text"
                  value={guestData.city}
                  onChange={(e) => setGuestData(prev => ({ ...prev, city: e.target.value }))}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ambre-500 focus:border-ambre-500"
                  placeholder={t('checkout.city')}
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">{t('checkout.postalCode')}</label>
                <input
                  type="text"
                  value={guestData.postal_code}
                  onChange={(e) => setGuestData(prev => ({ ...prev, postal_code: e.target.value }))}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ambre-500 focus:border-ambre-500"
                  placeholder={t('checkout.postalCode')}
                  required
                />
              </div>
            </div>
            
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="createAccount"
                checked={createAccount}
                onChange={(e) => setCreateAccount(e.target.checked)}
                className="w-4 h-4 text-ambre-600 border-gray-300 rounded focus:ring-ambre-500"
              />
              <label htmlFor="createAccount" className="text-sm text-gray-700">
                {t('checkout.createAccount')}
              </label>
            </div>
            
            {createAccount && (
              <div className="space-y-3 border-t border-gray-200 pt-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{t('checkout.password')}</label>
                  <div className="relative">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={accountData.password}
                      onChange={(e) => setAccountData(prev => ({ ...prev, password: e.target.value }))}
                      className="w-full px-4 py-3 pr-12 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ambre-500 focus:border-ambre-500"
                      placeholder={t('checkout.createPassword')}
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showPassword ? <EyeSlash size={20} /> : <Eye size={20} />}
                    </button>
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{t('checkout.confirmPassword')}</label>
                  <div className="relative">
                    <input
                      type={showConfirmPassword ? 'text' : 'password'}
                      value={accountData.confirmPassword}
                      onChange={(e) => setAccountData(prev => ({ ...prev, confirmPassword: e.target.value }))}
                      className="w-full px-4 py-3 pr-12 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ambre-500 focus:border-ambre-500"
                      placeholder={t('checkout.confirmPassword')}
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showConfirmPassword ? <EyeSlash size={20} /> : <Eye size={20} />}
                    </button>
                  </div>
                </div>
              </div>
            )}
            
            <button
              onClick={handleGuestContinue}
              disabled={loading}
              className="w-full px-6 py-3 bg-ambre-600 text-white rounded-lg hover:bg-ambre-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {createAccount ? t('checkout.continueAndCreate') : t('checkout.continue')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  // Step 2: Order Overview
  const renderStep2 = () => (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h2 className="text-2xl font-semibold text-gray-900 mb-2">{t('checkout.step2Title')}</h2>
        <p className="text-gray-600">{t('checkout.step2Subtitle')}</p>
      </div>
      
      <div className="grid md:grid-cols-2 gap-8">
        {/* Left Column - Order Summary */}
        <div className="bg-gray-50 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <ShoppingCart size={20} className="mr-2" />
            {t('checkout.orderSummary')}
          </h3>
          
          <div className="space-y-3">
            {items.map((item, index) => (
              <div key={index} className="flex justify-between items-center">
                <div className="flex items-center space-x-3">
                  <span className="text-gray-600">{item.quantity}x</span>
                  <span className="font-medium">{item.name}</span>
                </div>
                <span className="font-semibold">€{(item.price * item.quantity).toFixed(2)}</span>
              </div>
            ))}
              {(() => {
                const discountItems = getDiscountItems();
                if (discountItems.length === 0) return null;
                
                const totalDiscount = discountItems.reduce((sum, discount) => sum + discount.amount, 0);
                return (
                  <div className="flex justify-between items-center text-green-600">
                    <div className="flex items-center space-x-3">
                      <span className="font-medium">{t('checkout.discount')}</span>
                    </div>
                    <span className="font-semibold">-€{totalDiscount.toFixed(2)}</span>
                  </div>
                );
              })()}
            <div className="border-t border-gray-200 pt-3">
              <div className="flex justify-between items-center">
                <span className="text-gray-600">{t('checkout.subtotal')}</span>
                <span className="font-semibold">€{getCartSubtotal().toFixed(2)}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">{t('checkout.shipping')}</span>
                <span className="font-semibold">{getCartShipping() === 0 ? t('checkout.free') : `€${getCartShipping().toFixed(2)}`}</span>
              </div>
              <div className="flex justify-between items-center text-lg font-bold text-gray-900">
                <span>{t('checkout.total')}</span>
                <span>€{getCartTotal().toFixed(2)}</span>
              </div>
            </div>
          </div>
        </div>
        
        {/* Right Column - Shipping Address */}
        <div className="bg-gray-50 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <MapPin size={20} className="mr-2" />
            {t('checkout.shippingAddress')}
          </h3>
          
          <div className="text-sm text-gray-600 space-y-1">
            {user && userProfile ? (
              <>
                <p>{userProfile.first_name} {userProfile.last_name}</p>
                <p>{userProfile.street_and_housenumber}</p>
                {userProfile.address_line_2 && <p>{userProfile.address_line_2}</p>}
                <p>{userProfile.city}, {userProfile.postal_code}</p>
                <p>{userProfile.country}</p>
              </>
            ) : user && !userProfile ? (
              <p className="text-gray-500">{t('checkout.loadingAddress')}</p>
            ) : (
              <>
                <p>{guestData.first_name} {guestData.last_name}</p>
                <p>{guestData.street_and_housenumber}</p>
                <p>{guestData.city}, {guestData.postal_code}</p>
                <p>{guestData.country}</p>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  // Step 3: Payment
  const renderStep3 = () => {
    return (
      <div className="space-y-4 md:space-y-6">
        <div className="text-center mb-6 md:mb-8">
          <h2 className="text-xl md:text-2xl font-semibold text-gray-900 mb-2">{t('checkout.step3Title')}</h2>
        </div>
        
        {error && (
          <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-red-700 text-sm">{error}</p>
          </div>
        )}
        
        <div className="bg-gray-50 rounded-lg p-4 md:p-6">
          <h3 className="text-base md:text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <Bank size={18} className="mr-2" />
            {t('checkout.paymentDetails')}
          </h3>

          <div className="mb-4 md:mb-6">
            <div className="flex justify-between items-center text-sm text-gray-600 mb-2">
              <span>{t('checkout.orderTotal')}:</span>
              <span className="font-semibold text-base md:text-lg text-gray-900">€{getCartTotal().toFixed(2)}</span>
            </div>
          </div>

          {getCartTotal() > 0 && (
            <BankTransferPayment
              orderNumber={orderNumber}
              totalAmount={getCartTotal()}
              loading={loading}
              onConfirm={handleConfirmBankTransfer}
              onCancel={() => setCurrentStep(2)}
            />
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="w-full max-w-4xl mx-auto bg-white rounded-xl shadow-lg">
      {/* Step Indicator */}
      <div className="border-b border-gray-200 p-6">
        <div className="flex items-start justify-center">
          {steps.map((step, index) => (
            <div key={step.id} className="flex items-start">
              <div className="flex flex-col items-center">
                <div className={`flex items-center justify-center w-8 h-8 rounded-full border-2 ${
                  currentStep >= step.id 
                    ? 'bg-ambre-600 border-ambre-600 text-white' 
                    : 'border-gray-300 text-gray-500'
                }`}>
                  {currentStep > step.id ? (
                    <CheckCircle size={16} />
                  ) : (
                    <span className="font-semibold text-xs">{step.id}</span>
                  )}
                </div>
                <div className="mt-3 text-center">
                  <div className={`text-sm font-medium ${
                    currentStep >= step.id ? 'text-ambre-600' : 'text-gray-500'
                  }`}>
                    {step.title}
                  </div>
                </div>
              </div>
              {index < steps.length - 1 && (
                <div className={`w-8 md:w-20 h-0.5 mx-2 md:mx-8 self-center ${
                  currentStep > step.id ? 'bg-ambre-600' : 'bg-gray-300'
                }`} />
              )}
            </div>
          ))}
        </div>
      </div>
      
      {/* Step Content */}
      <div className="p-6">
        {currentStep === 1 && renderStep1()}
        {currentStep === 2 && renderStep2()}
        {currentStep === 3 && renderStep3()}
      </div>

      {/* Navigation */}
      <div className="border-t border-gray-200 p-6">
        <div className="flex justify-between items-center">
          <button
            onClick={currentStep === 1 ? onCancel : handleBack}
            disabled={loading}
            className="flex items-center px-6 py-3 text-gray-600 hover:text-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <ArrowLeft size={20} className="mr-2" />
            {currentStep === 1 ? t('checkout.cancel') : t('checkout.back')}
          </button>

          <div className="flex space-x-3">
            {currentStep === 2 && (
              <button
                onClick={handleProceedToPayment}
                disabled={!orderNumber}
                className="flex items-center px-6 py-3 bg-ambre-600 text-white rounded-lg hover:bg-ambre-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {t('checkout.proceedToPayment')}
                <ArrowRight size={20} className="ml-2" />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CheckoutFlow; 